// Bill totals, the ZATCA way, used by every sales and purchase screen, the POS
// till and the prints. The API's ZATCA e-invoice uses the same steps
// (Axobis.Restaurant.Business/Zatca/SalesTotals.cs), so the screen, the saved
// bill, the print and the e-invoice agree to the last halala.
//
//   1. each line: gross = qty × price, line discount, taxable = gross − discount
//   2. lines are grouped by VAT rate
//   3. the bill discount and the addition are shared over the rates by taxable
//      amount: they change the taxable amount (VAT is charged on what is left)
//   4. VAT is worked out once per rate on (taxable − discount share + addition share)
//   5. each rate's VAT is spread back over its lines, so the lines add up to it
//   6. net = taxable − bill discount + addition + VAT − round off
//
// Every amount is rounded to `decimals` (2 for SAR) at each step.

const roundTo = (value, decimals) => {
  const n = Number(value) || 0;
  const f = 10 ** decimals;
  return (Math.sign(n) * Math.round(Math.abs(n) * f + 1e-9)) / f;
};

const num = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

/** Shares `amount` over `weights` in proportion; the last share takes the remainder. */
const share = (amount, weights, r) => {
  const total = weights.reduce((s, w) => s + w, 0);
  if (!amount || !total) return weights.map(() => 0);
  let given = 0;
  return weights.map((w, i) => {
    if (i === weights.length - 1) return r(amount - given);
    const s = r((amount * w) / total);
    given += s;
    return s;
  });
};

/**
 * @param {Array<{quantity, price, discountPercent?, discountAmount?, taxPercent}>} lines
 *   discountAmount wins over discountPercent when given
 * @param {{billDiscount?, addition?, roundOff?, decimals?}} options
 * @returns {{ lines: Array<{gross, discount, taxable, taxPercent, tax, net}>,
 *            rates: Array<{taxPercent, taxable, discount, addition, tax}>,
 *            gross, lineDiscount, billDiscount, addition, discountTotal, taxable, tax, roundOff, net }}
 */
export const calcBillTotals = (lines, { billDiscount = 0, addition = 0, roundOff = 0, decimals = 2 } = {}) => {
  const d = Number.isInteger(decimals) && decimals >= 0 ? decimals : 2;
  const r = (v) => roundTo(v, d);

  const rows = (lines || []).map((l) => {
    const gross = r(num(l?.quantity) * num(l?.price));
    const discount =
      l?.discountAmount !== undefined && l?.discountAmount !== null && l?.discountAmount !== ""
        ? r(num(l.discountAmount))
        : r((gross * num(l?.discountPercent)) / 100);
    const taxPercent = Math.max(0, num(l?.taxPercent));
    return { gross, discount, taxable: r(gross - discount), taxPercent, tax: 0, net: 0 };
  });

  // VAT rates, largest taxable first (ties: higher rate first)
  const rateMap = new Map();
  rows.forEach((row, i) => {
    const key = row.taxPercent;
    if (!rateMap.has(key)) rateMap.set(key, { taxPercent: key, taxable: 0, lines: [] });
    const rate = rateMap.get(key);
    rate.taxable = r(rate.taxable + row.taxable);
    rate.lines.push(i);
  });
  const rates = [...rateMap.values()].sort((a, b) => b.taxable - a.taxable || b.taxPercent - a.taxPercent);

  const bd = r(num(billDiscount));
  const add = r(num(addition));
  const discountShares = share(bd, rates.map((x) => x.taxable), r);
  const additionShares = share(add, rates.map((x) => x.taxable), r);

  rates.forEach((rate, i) => {
    rate.discount = discountShares[i];
    rate.addition = additionShares[i];
    rate.tax = r(((rate.taxable - rate.discount + rate.addition) * rate.taxPercent) / 100);
    // spread the rate's VAT over its lines by taxable amount
    const lineTax = share(rate.tax, rate.lines.map((li) => rows[li].taxable), r);
    if (!rate.taxable && rate.lines.length) lineTax[lineTax.length - 1] = rate.tax;
    rate.lines.forEach((li, k) => {
      rows[li].tax = lineTax[k];
      rows[li].net = r(rows[li].taxable + rows[li].tax);
    });
  });

  const gross = r(rows.reduce((s, x) => s + x.gross, 0));
  const lineDiscount = r(rows.reduce((s, x) => s + x.discount, 0));
  const taxable = r(rows.reduce((s, x) => s + x.taxable, 0));
  const tax = r(rates.reduce((s, x) => s + x.tax, 0));
  const ro = r(num(roundOff));

  return {
    lines: rows,
    rates: rates.map(({ lines: _l, ...rest }) => rest),
    gross,
    lineDiscount,
    billDiscount: bd,
    addition: add,
    discountTotal: r(lineDiscount + bd),
    taxable,
    tax,
    roundOff: ro,
    net: r(taxable - bd + add + tax - ro),
  };
};
